import { useMemo } from "react";
import {
  findGoverningCase,
  allowableOverpressurePercent,
  type ContingencyKind,
  type InstallationKind,
} from "../calc/governingCase";
import {
  API_521_SCENARIOS,
  defaultContingency,
  findScenario,
  isNotPrdScenario,
  scenarioLabel,
} from "../data/scenarios";
import {
  FLUID_LABELS,
  newScenarioRowIn,
  syncRateCalcKind,
  toPsvCase,
  type CaseState,
  type ScenarioRow,
  type SimpleFluidKind,
} from "./caseState";
import { Field, Help, Item } from "./formBits";
import { RateCalcPanel } from "./RateCalcPanel";
import { RateGuidancePanel } from "./RateGuidancePanel";
import { RATE_CALC_ITEMS, rateCalcContext } from "./rateCalc";
import { gaugeLabel, type PressureUnit } from "./pressureUnit";

/**
 * 밸브 하나를 놓고 과압 시나리오를 비교해 지배 케이스와 규격을 확정하는 화면.
 * 여러 밸브 관리는 App 이 하고, 이 화면은 선택된 한 건만 다룬다.
 */
export function CaseWorkflowPanel({
  value,
  unit,
  onChange,
}: {
  value: CaseState;
  /** 화면 입력 압력 단위. 계산 직전에 kPa 로 환산된다. */
  unit: PressureUnit;
  onChange: (next: CaseState) => void;
}) {
  const patch = (p: Partial<CaseState>) => onChange({ ...value, ...p });
  const patchRow = (id: string, p: Partial<ScenarioRow>) =>
    patch({
      rows: value.rows.map((r) => (r.id === id ? { ...r, ...p } : r)),
    });

  const psvCase = useMemo(() => toPsvCase(value, unit), [value, unit]);
  const result = useMemo(() => findGoverningCase(psvCase), [psvCase]);

  /** 유량 기준 1위 — 면적 기준과 다르면 그 사실을 알려주기 위해 쓴다. */
  const topByFlow = useMemo(() => {
    const candidates = result.ranked.filter((e) => e.requiredAreaMm2 !== null);
    if (candidates.length === 0) return null;
    // 단위가 섞이면 유량 비교 자체가 성립하지 않으므로 그때는 비교하지 않는다.
    const units = new Set(candidates.map((e) => e.flow.unit));
    if (units.size > 1) return null;
    return candidates.reduce((a, b) => (b.flow.value > a.flow.value ? b : a));
  }, [result]);

  const flowDiffersFromArea =
    topByFlow !== null &&
    result.governing !== null &&
    topByFlow.entry.id !== result.governing.entry.id;

  return (
    <>
      <section className="card">
        <h2>검토 대상 밸브</h2>
        <div className="grid-form">
          <Field label="태그" unit="" help="tag">
            <input
              value={value.tag}
              onChange={(e) => patch({ tag: e.target.value })}
            />
          </Field>
          <Field label="보호 설비" unit="" help="equipment">
            <input
              value={value.equipment}
              placeholder="예: 탈에탄기 환류드럼"
              onChange={(e) => patch({ equipment: e.target.value })}
            />
          </Field>
          <Field label="설정압" unit={gaugeLabel(unit)} help="setPressure">
            <input
              value={value.setPressure}
              onChange={(e) => patch({ setPressure: e.target.value })}
            />
          </Field>
          <Field label="설치 형태" unit="" help="installation">
            <select
              value={value.installation}
              onChange={(e) =>
                patch({ installation: e.target.value as InstallationKind })
              }
            >
              <option value="single">단일 설치 (운전 과압 10 %)</option>
              <option value="multiple">복수 설치 (운전 과압 16 %)</option>
            </select>
          </Field>
        </div>
        <p className="hint">
          허용 과압은 API 520 Part I 5.4 에 따라 자동 적용됩니다 — 운전 상황은
          설치 형태에 따라 10 % 또는 16 %, 외부 화재는 21 % 입니다.
        </p>
      </section>

      <section className="card">
        <div className="row-head">
          <h2>과압 시나리오</h2>
          <button
            type="button"
            className="btn"
            onClick={() => patch({ rows: [...value.rows, newScenarioRowIn(unit)] })}
          >
            + 시나리오 추가
          </button>
        </div>

        {value.rows.length === 0 && (
          <p className="hint">시나리오를 추가하십시오.</p>
        )}

        {value.rows.map((r) => {
          const def = findScenario(r.item);
          const notPrd = isNotPrdScenario(r.item);
          const evaluated = result.ranked.find((e) => e.entry.id === r.id);
          const isGoverning = result.governing?.entry.id === r.id;
          return (
            <div
              key={r.id}
              className={"scenario" + (isGoverning ? " scenario--gov" : "")}
            >
              <div className="scenario__head">
                <label className="scenario__check">
                  <input
                    type="checkbox"
                    checked={r.included}
                    onChange={(e) =>
                      patchRow(r.id, { included: e.target.checked })
                    }
                  />
                  검토 포함
                  <Help topic="includedCheck" />
                </label>
                <select
                  className="scenario__select"
                  value={r.item}
                  onChange={(e) => {
                    const item = Number(e.target.value);
                    patchRow(r.id, {
                      item,
                      contingency: defaultContingency(item),
                      rateCalc: syncRateCalcKind(r.rateCalc, item),
                    });
                  }}
                >
                  {API_521_SCENARIOS.map((s) => (
                    <option key={s.item} value={s.item}>
                      {scenarioLabel(s)}
                    </option>
                  ))}
                </select>
                <Help topic="scenarioItem" />
                {isGoverning && <span className="badge">지배 케이스</span>}
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() =>
                    patch({ rows: value.rows.filter((x) => x.id !== r.id) })
                  }
                >
                  삭제
                </button>
              </div>

              <label className="scenario__desc">
                <span className="grid-form__label">
                  과압 상황 설명 <em className="muted">계산서·PSM 기재용</em>
                  <Help topic="scenarioDescription" />
                </span>
                <textarea
                  rows={2}
                  value={r.description}
                  placeholder="이 설비에서 구체적으로 어떤 상황인지 적어 주십시오. 예: 하류 차단밸브 오조작으로 배출 경로 폐쇄, 공급 펌프는 계속 운전"
                  onChange={(e) =>
                    patchRow(r.id, { description: e.target.value })
                  }
                />
              </label>

              {notPrd && (
                <p className="scenario__warn">
                  이 시나리오는 일반 안전밸브로 대응하는 대상이 아닙니다(API 521
                  Table 1). 상황 자체를 회피하는 설계로 대응해야 합니다.
                </p>
              )}
              {RATE_CALC_ITEMS.has(r.item) && (
                <p className="hint">
                  이 시나리오는 아래 계산기로 소요 분출용량을 구할 수
                  있습니다. (13·14·15번은 API 521 의 식, 그 외는 표준식이
                  아닌 일반 유체역학 계산입니다 — 계산기 안에 표시됩니다.)
                </p>
              )}

              <div className="grid-form">
                <Field label="상황 구분" unit="" help="contingency">
                  <select
                    value={r.contingency}
                    onChange={(e) =>
                      patchRow(r.id, {
                        contingency: e.target.value as ContingencyKind,
                      })
                    }
                  >
                    <option value="operating">
                      운전 (
                      {allowableOverpressurePercent(
                        "operating",
                        value.installation,
                      )}{" "}
                      %)
                    </option>
                    <option value="fire">화재 (21 %)</option>
                  </select>
                </Field>
                <Field label="유체 상태" unit="" help="fluidKind">
                  <select
                    value={r.fluidKind}
                    onChange={(e) =>
                      patchRow(r.id, {
                        fluidKind: e.target.value as SimpleFluidKind,
                      })
                    }
                  >
                    {(Object.keys(FLUID_LABELS) as SimpleFluidKind[]).map((k) => (
                      <option key={k} value={k}>
                        {FLUID_LABELS[k]}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="소요 분출용량"
                  unit={r.fluidKind === "liquid" ? "L/min" : "kg/h"}
                  help={r.fluidKind === "liquid" ? "flowVolume" : "flowMass"}
                >
                  <input
                    value={r.flow}
                    onChange={(e) => patchRow(r.id, { flow: e.target.value })}
                  />
                </Field>
                <Field label="배압" unit={gaugeLabel(unit)} help="backpressureGauge">
                  <input
                    value={r.backpressure}
                    onChange={(e) =>
                      patchRow(r.id, { backpressure: e.target.value })
                    }
                  />
                </Field>
                {r.fluidKind === "gas" && (
                  <>
                    <Field label="온도" unit="K" help="temperatureK">
                      <input
                        value={r.temperature}
                        onChange={(e) =>
                          patchRow(r.id, { temperature: e.target.value })
                        }
                      />
                    </Field>
                    <Field label="분자량" unit="kg/kmol" help="molecularWeight">
                      <input
                        value={r.molecularWeight}
                        onChange={(e) =>
                          patchRow(r.id, { molecularWeight: e.target.value })
                        }
                      />
                    </Field>
                    <Field label="비열비 k" unit="" help="specificHeatRatio">
                      <input
                        value={r.specificHeatRatio}
                        onChange={(e) =>
                          patchRow(r.id, { specificHeatRatio: e.target.value })
                        }
                      />
                    </Field>
                    <Field label="압축계수 Z" unit="" help="compressibility">
                      <input
                        value={r.compressibility}
                        onChange={(e) =>
                          patchRow(r.id, { compressibility: e.target.value })
                        }
                      />
                    </Field>
                  </>
                )}
                {r.fluidKind === "liquid" && (
                  <Field label="비중" unit="" help="specificGravity">
                    <input
                      value={r.specificGravity}
                      onChange={(e) =>
                        patchRow(r.id, { specificGravity: e.target.value })
                      }
                    />
                  </Field>
                )}
              </div>

              <RateGuidancePanel item={r.item} fluidKind={r.fluidKind} />

              {RATE_CALC_ITEMS.has(r.item) && (
                <RateCalcPanel
                  item={r.item}
                  state={r.rateCalc}
                  unit={unit}
                  ctx={rateCalcContext(r, value, unit)}
                  onChange={(next) => patchRow(r.id, { rateCalc: next })}
                  // 넣은 값을 함께 기록해 둔다 — 계산서가 "이 유량은
                  // 계산기로 구했다"를 알 수 있어야 근거를 실을 수 있다.
                  onApply={(flow) =>
                    patchRow(r.id, {
                      flow,
                      rateCalc: { ...r.rateCalc, appliedFlow: flow },
                    })
                  }
                />
              )}

              {r.included && evaluated && (
                <p
                  className={
                    evaluated.error ? "scenario__warn" : "scenario__result"
                  }
                >
                  {evaluated.error ??
                    `소요 면적 ${evaluated.requiredAreaMm2!.toLocaleString("ko-KR", { maximumFractionDigits: 0 })} mm² · 과압 ${evaluated.overpressurePercent} % · P1 ${evaluated.relievingPressureKpaAbs.toFixed(1)} kPa abs · ${evaluated.equation}`}
                </p>
              )}
            </div>
          );
        })}
      </section>

      <section className="card">
        <h2>지배 케이스 및 선정 결과</h2>

        {result.governing === null ? (
          <div className="result result--error" role="alert">
            계산에 성공한 시나리오가 없습니다. 입력값을 확인하십시오.
          </div>
        ) : (
          <div className="result result--ok">
            <div className="result__headline">
              <span className="result__label">지배 케이스</span>
              <strong className="result__value">
                {result.governing.entry.item}. {result.governing.entry.label}
              </strong>
            </div>
            {result.governing.entry.description && (
              <p className="muted" style={{ marginTop: 6 }}>
                {result.governing.entry.description}
              </p>
            )}
            <dl className="result__grid">
              <Item
                label="소요 분출용량"
                value={`${result.governing.flow.value.toLocaleString("ko-KR")} ${result.governing.flow.unit}`}
              />
              <Item
                label="소요 면적"
                value={`${result.governing.requiredAreaMm2!.toLocaleString("ko-KR", { maximumFractionDigits: 0 })} mm²`}
              />
              <Item
                label="적용 과압"
                value={`${result.governing.overpressurePercent} %`}
              />
              <Item
                label="배출 압력 P1"
                value={`${result.governing.relievingPressureKpaAbs.toFixed(1)} kPa abs`}
              />
              <Item label="적용 식" value={result.governing.equation ?? "—"} />
              {result.selection?.ok && (
                <>
                  <Item
                    label="선정 오리피스"
                    value={
                      result.selection.orifice.designation +
                      (result.selection.valveCount > 1
                        ? ` × ${result.selection.valveCount}개 병렬`
                        : "")
                    }
                  />
                  <Item
                    label="여유율"
                    value={`${result.selection.marginPct.toFixed(1)} %`}
                  />
                </>
              )}
            </dl>
            {result.selection && !result.selection.ok && (
              <p className="scenario__warn">{result.selection.message}</p>
            )}
          </div>
        )}

        {flowDiffersFromArea && (
          <div className="notice" role="note">
            <strong>유량 기준과 면적 기준의 지배 케이스가 다릅니다.</strong>
            <br />
            분출용량은 <b>
              {topByFlow.entry.item}. {topByFlow.entry.label}
            </b>{" "}
            ({topByFlow.flow.value.toLocaleString("ko-KR")}{" "}
            {topByFlow.flow.unit})이 가장 크지만, 허용 과압이 달라 배출 압력
            P1 이 다르기 때문에 <b>소요 면적</b>은{" "}
            <b>
              {result.governing!.entry.item}. {result.governing!.entry.label}
            </b>{" "}
            이 더 큽니다. 밸브 용량은 면적 × 배출압에 비례하므로, 면적이 큰
            쪽으로 선정하면 나머지 케이스는 자동으로 커버됩니다(API 520 Part I
            5.4.3.1.2).
          </div>
        )}

        <h3>시나리오 비교 (소요 면적 내림차순)</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>시나리오</th>
                <th>상황</th>
                <th>분출용량</th>
                <th>과압 %</th>
                <th>P1 (kPa abs)</th>
                <th>소요 면적 (mm²)</th>
                <th>판정</th>
              </tr>
            </thead>
            <tbody>
              {result.ranked.map((e) => (
                <tr
                  key={e.entry.id}
                  className={
                    e.entry.id === result.governing?.entry.id
                      ? "row--selected"
                      : undefined
                  }
                >
                  <td>
                    {e.entry.item}. {e.entry.label}
                  </td>
                  <td>{e.entry.contingency === "fire" ? "화재" : "운전"}</td>
                  <td>
                    {e.flow.value.toLocaleString("ko-KR")}{" "}
                    <span className="muted">{e.flow.unit}</span>
                  </td>
                  <td>{e.overpressurePercent}</td>
                  <td>{e.relievingPressureKpaAbs.toFixed(1)}</td>
                  <td>
                    {e.requiredAreaMm2 === null
                      ? "—"
                      : e.requiredAreaMm2.toLocaleString("ko-KR", {
                          maximumFractionDigits: 0,
                        })}
                  </td>
                  <td>
                    {e.error
                      ? "계산 불가"
                      : e.entry.id === result.governing?.entry.id
                        ? "지배"
                        : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(result.excludedCount > 0 || result.failedCount > 0) && (
          <p className="hint">
            {result.excludedCount > 0 &&
              `검토 제외 ${result.excludedCount}건. `}
            {result.failedCount > 0 && `계산 불가 ${result.failedCount}건.`}
          </p>
        )}

        {result.ranked.some((e) => e.warnings.length > 0) && (
          <ul className="warnings">
            {result.ranked.flatMap((e) =>
              e.warnings.map((w) => (
                <li key={`${e.entry.id}-${w}`}>
                  [{e.entry.item}. {e.entry.label}] {w}
                </li>
              )),
            )}
          </ul>
        )}
      </section>
    </>
  );
}
